import React, { useState, useEffect, useMemo } from 'react';
import Button from './Button';
import PillDropdown from './PillDropdown';
import Checkbox from './Checkbox';

export default function JiraMappingModal({
  parsedData,
  epicMappings,
  availableEpics,
  strategy,
  onMappingChange,
  onImport,
  onCancel,
}) {
  const [taskMappings, setTaskMappings] = useState(new Map()); // task issueKey -> subepicId
  const [localEpicMappings, setLocalEpicMappings] = useState(epicMappings || []);
  const [replaceAllWork, setReplaceAllWork] = useState(false);
  
  // Initialize task mappings based on parent relationships
  // Use epic index as identifier since subepics don't exist yet
  useEffect(() => {
    if (!parsedData || !localEpicMappings || localEpicMappings.length === 0) return;
    
    const initialTaskMappings = new Map();
    
    // Map tasks to epic indices (which will become subepics)
    localEpicMappings.forEach((mapping, epicIndex) => {
      const { jiraEpic } = mapping;
      if (jiraEpic && parsedData.parentChildMap) {
        const children = parsedData.parentChildMap.get(jiraEpic.issueKey) || [];
        children.forEach(child => {
          // Use epic index as temporary identifier
          initialTaskMappings.set(child.issueKey, `epic_${epicIndex}`);
        });
      }
    });
    
    setTaskMappings(initialTaskMappings);
  }, [parsedData, localEpicMappings, strategy]);
  
  // Get all subepics for task mapping dropdown (using epic indices as IDs)
  const subepicOptions = useMemo(() => {
    const subepics = [];
    localEpicMappings.forEach((mapping, index) => {
      subepics.push({
        id: `epic_${index}`,
        value: mapping.jiraEpic.summary,
      });
    });
    return subepics;
  }, [localEpicMappings]);
  
  // Validation: check if all tasks have mappings
  const isValid = useMemo(() => {
    if (!parsedData || !parsedData.stories || !parsedData.tasks) return false;
    const allTasks = [...parsedData.stories, ...parsedData.tasks];
    return allTasks.length > 0 && allTasks.every(task => taskMappings.has(task.issueKey));
  }, [parsedData, taskMappings]);
  
  // Get orphaned tasks (no parent)
  const orphanedTasks = useMemo(() => {
    if (!parsedData || !parsedData.stories || !parsedData.tasks) return [];
    return [...parsedData.stories, ...parsedData.tasks].filter(
      task => !task.parent && !taskMappings.has(task.issueKey)
    );
  }, [parsedData, taskMappings]);
  
  const handleEpicMappingChange = (index, newTargetEpic) => {
    const updated = [...localEpicMappings];
    updated[index] = {
      ...updated[index],
      targetEpic: newTargetEpic,
    };
    setLocalEpicMappings(updated);
    if (onMappingChange) {
      onMappingChange(updated);
    }
  };
  
  const handleTaskMappingChange = (taskIssueKey, subepicId) => {
    const updated = new Map(taskMappings);
    if (subepicId) {
      updated.set(taskIssueKey, subepicId);
    } else {
      updated.delete(taskIssueKey);
    }
    setTaskMappings(updated);
  };
  
  const handleImport = () => {
    if (!isValid) {
      alert('Please map all tasks to subepics before importing.');
      return;
    }
    
    if (onImport) {
      // Ensure taskMappings is passed as a Map
      const taskMappingsToPass = taskMappings instanceof Map ? taskMappings : new Map(Object.entries(taskMappings || {}));
      onImport({
        epicMappings: localEpicMappings,
        taskMappings: taskMappingsToPass,
        replaceAllWork,
      });
    }
  };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxHeight: '80vh', overflow: 'auto' }}>
      <h2 style={{ color: '#000', marginTop: -11 }}>Review JIRA Import Mappings</h2>
      
      {strategy === 'all_to_subepics' && parsedData && parsedData.epics && (
        <div style={{ 
          background: '#fff3cd', 
          padding: 12, 
          borderRadius: 8, 
          border: '1px solid #ffc107',
          color: '#856404'
        }}>
          <strong>Epic Overflow Detected:</strong> All {parsedData.epics.length} JIRA Epics will be imported as Peepz Subepics.
        </div>
      )}
      
      {orphanedTasks.length > 0 && (
        <div style={{ 
          background: '#f8d7da', 
          padding: 12, 
          borderRadius: 8, 
          border: '1px solid #dc3545',
          color: '#721c24'
        }}>
          <strong>Warning:</strong> {orphanedTasks.length} task(s) have no parent mapping. Please assign them to a subepic.
        </div>
      )}
      
      {/* Epics Section */}
      {parsedData && parsedData.epics && parsedData.epics.length > 0 && (
        <div>
          <h3 style={{ color: '#000', fontSize: 16, marginBottom: 12 }}>
            Epics ({parsedData.epics.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {localEpicMappings.map((mapping, index) => {
              const { jiraEpic, targetEpic, type } = mapping;
              return (
                <div 
                  key={jiraEpic.issueKey} 
                  style={{ 
                    padding: 12, 
                    background: '#f5f5f5', 
                    borderRadius: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}
                >
                  <div style={{ fontWeight: 'bold', color: '#000' }}>
                    {jiraEpic.summary}
                  </div>
                  {jiraEpic.description && (
                    <div style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                      {jiraEpic.description.substring(0, 100)}{jiraEpic.description.length > 100 ? '...' : ''}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12, color: '#666' }}>Will become:</span>
                    {type === 'subepic' ? (
                      <PillDropdown
                        list={availableEpics.map(e => ({ id: e.id || null, value: e.title }))}
                        selectedId={targetEpic?.id || null}
                        placeholder="Select target epic..."
                        onSelect={(item) => {
                          const selectedEpic = availableEpics.find(e => e.id === item.id);
                          if (selectedEpic) {
                            handleEpicMappingChange(index, selectedEpic);
                          }
                        }}
                        pillStyle={{ minWidth: 200 }}
                      />
                    ) : (
                      <span style={{ color: '#000', fontWeight: 'bold' }}>New Peepz Epic</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Tasks Section */}
      {parsedData && parsedData.stories && parsedData.tasks && ([...parsedData.stories, ...parsedData.tasks].length > 0) && (
        <div>
          <h3 style={{ color: '#000', fontSize: 16, marginBottom: 12 }}>
            Tasks ({[...parsedData.stories, ...parsedData.tasks].length} - from JIRA Stories and Tasks)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '400px', overflow: 'auto' }}>
            {[...parsedData.stories, ...parsedData.tasks].map((task) => {
              const currentMapping = taskMappings.get(task.issueKey);
              const mappedEpicIndex = currentMapping ? parseInt(currentMapping.replace('epic_', '')) : -1;
              const mappedEpic = mappedEpicIndex >= 0 ? localEpicMappings[mappedEpicIndex] : null;
              
              return (
                <div 
                  key={task.issueKey} 
                  style={{ 
                    padding: 12, 
                    background: currentMapping ? '#f5f5f5' : '#fff3cd', 
                    borderRadius: 8,
                    border: currentMapping ? 'none' : '1px solid #ffc107',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8
                  }}
                >
                  <div style={{ fontWeight: 'bold', color: '#000' }}>
                    {task.summary}
                  </div>
                  {task.parent && (
                    <div style={{ fontSize: 11, color: '#666' }}>
                      Parent: {task.parentSummary || task.parent}
                    </div>
                  )}
                  {mappedEpic && (
                    <div style={{ fontSize: 11, color: '#28a745', fontWeight: 'bold' }}>
                      → Will be under: {mappedEpic.jiraEpic.summary}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12, color: '#666' }}>Map to subepic:</span>
                    <PillDropdown
                      list={subepicOptions}
                      selectedId={currentMapping || null}
                      placeholder="Select subepic..."
                      onSelect={(item) => {
                        handleTaskMappingChange(task.issueKey, item.id);
                      }}
                      pillStyle={{ minWidth: 200 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Replace All Work Option */}
      <div style={{ 
        marginTop: 20, 
        padding: 16, 
        background: '#f8f9fa', 
        borderRadius: 8,
        border: '1px solid #dee2e6'
      }}>
        <Checkbox
          text="Replace all current work in this project"
          checked={replaceAllWork}
          onChangeCb={(e) => setReplaceAllWork(e.target.checked)}
        />
        <div style={{ fontSize: 12, color: '#666', marginTop: 8, marginLeft: 24 }}>
          When checked, all existing epics, subepics, and tasks will be deleted before importing the JIRA data. Stories will be marked as complete instead of deleted.
        </div>
      </div>
      
      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
        <Button text="Cancel" callback={onCancel} />
        <Button 
          text="Import" 
          callback={handleImport} 
          color="purple" 
          disabled={!isValid}
        />
      </div>
    </div>
  );
}

